import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/Card/Card';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import { useAuth } from '@/context/AuthContext';
import {
  coletaPesosKeys,
  fetchProdutosColetaPesos,
  submitColetaPesos,
} from '@/services/coletaPesosService';
import type {
  IContainerForm,
  IColetaPesosPayloadItem,
  IColetaPesosProduto,
  IPesoTotals,
  TBarcodeSource,
  TDebugMessageType,
} from '@/types/coletaPesos';
import './coleta-pesos.css';

export const Route = createFileRoute('/_authenticated/movimentacao/coleta-pesos/')({
  component: ColetaPesosPage,
});

const PESOS_TABLE_COLUMNS = ['Sequencial', 'Peso Unitário (kg)', 'Observação', 'Ação'];

const CLASSIFICACAO_OPTIONS = ['Normal', 'Especial', 'Quarentena', 'Reprocesso'];

type TContainerScalarField = Exclude<
  keyof IContainerForm,
  'pesos' | 'codigo_caixa' | 'num_pecas' | 'barcodeValue' | 'description'
>;

function getInitialContainer(): IContainerForm {
  return {
    nf_ref: '',
    peso_nota: '',
    codigo_produto: '',
    description: '',
    lote: '',
    validade: '',
    cliente: '',
    classificacao: 'Normal',
    pesos: [],
    codigo_caixa: '',
    num_pecas: '',
    barcodeValue: '',
  };
}

function computeTotals(pesos: IContainerForm['pesos']): IPesoTotals {
  let total = 0;
  let count = 0;
  pesos.forEach((p) => {
    const num = parseFloat(p.peso_liquido);
    if (!isNaN(num)) {
      total += num;
      count++;
    }
  });
  return { total, count };
}

function converterData(dateStr: string): string {
  const dia = dateStr.substring(4, 6);
  const mes = dateStr.substring(2, 4);
  const ano = `20${dateStr.substring(0, 2)}`;

  const date = new Date(Date.UTC(parseInt(ano, 10), parseInt(mes, 10) - 1, parseInt(dia, 10)));
  date.setDate(date.getDate() + 21);
  return date.toISOString().split('T')[0];
}

function buildPayload(containers: IContainerForm[]): IColetaPesosPayloadItem[] {
  const payload: IColetaPesosPayloadItem[] = [];

  containers.forEach((container) => {
    const {
      nf_ref,
      peso_nota,
      codigo_produto,
      description,
      lote,
      cliente,
      classificacao,
      validade,
      pesos,
    } = container;

    const notaValue = parseFloat(peso_nota) || 0;
    const { total } = computeTotals(pesos);
    const diferenca = notaValue - total;

    pesos.forEach(({ peso_liquido, codigo_barras }) => {
      if (peso_liquido !== '') {
        payload.push({
          nf_ref,
          peso_nota: notaValue,
          codigo_produto,
          description,
          lote,
          cliente,
          classificacao,
          validade,
          peso_liquido: parseFloat(peso_liquido),
          diferenca,
          codigo_barras: codigo_barras || null,
        });
      }
    });
  });

  return payload;
}

function formatContainerNumber(index: number): string {
  return String(index + 1).padStart(3, '0');
}

function ColetaPesosPage() {
  const { user } = useAuth();
  const [containers, setContainers] = useState<IContainerForm[]>([getInitialContainer()]);
  const [pageMessage, setPageMessage] = useState('');
  const [readBoxes, setReadBoxes] = useState<Set<string>>(new Set());
  const [debugMessage, setDebugMessage] = useState('');
  const [debugMessageType, setDebugMessageType] = useState<TDebugMessageType>('info');
  const [sendToWarehouse, setSendToWarehouse] = useState(false);
  const [source, setSource] = useState<TBarcodeSource>('Manual');

  const showDebugMessage = useCallback((msg: string, type: TDebugMessageType = 'info') => {
    setDebugMessage(msg);
    setDebugMessageType(type);
    setTimeout(() => setDebugMessage(''), 3000);
  }, []);

  const {
    data: productList = [],
    isError: isProductListError,
    error: productListError,
  } = useQuery({
    queryKey: coletaPesosKeys.produtos(),
    queryFn: fetchProdutosColetaPesos,
  });

  useEffect(() => {
    if (isProductListError) {
      const msg =
        productListError instanceof Error
          ? productListError.message
          : 'Erro ao carregar lista de produtos.';
      showDebugMessage(msg, 'error');
    }
  }, [isProductListError, productListError, showDebugMessage]);

  const submitMutation = useMutation({
    mutationFn: submitColetaPesos,
    onSuccess: () => {
      setPageMessage('Dados inseridos com sucesso!');
      if (window.confirm('Dados inseridos com sucesso! Clique OK para limpar os campos.')) {
        setContainers([getInitialContainer()]);
        setReadBoxes(new Set());
      }
    },
    onError: (error: Error) => {
      showDebugMessage(error.message || 'Erro ao enviar os dados.', 'error');
    },
  });

  const handleCommonChange = (index: number, field: TContainerScalarField, value: string) => {
    const updated = [...containers];
    updated[index][field] = value;
    setContainers(updated);
  };

  const updateProductDescription = (index: number, code: string) => {
    const found = productList.find((prod: IColetaPesosProduto) => prod.nome === code);
    const description = found ? found.descricao : 'Produto não localizado';
    const updated = [...containers];
    updated[index].description = description;
    setContainers(updated);
  };

  const handlePesoChange = (containerIndex: number, pesoIndex: number, value: string) => {
    const updated = [...containers];
    updated[containerIndex].pesos[pesoIndex].peso_liquido = value;
    setContainers(updated);
  };

  const addPesoInput = (containerIndex: number) => {
    const updated = [...containers];
    updated[containerIndex].pesos.push({ peso_liquido: '', codigo_barras: '' });
    setContainers(updated);
  };

  const removePesoInput = (containerIndex: number, pesoIndex: number) => {
    const updated = [...containers];
    const removed = updated[containerIndex].pesos[pesoIndex];
    if (removed?.codigo_barras) {
      setReadBoxes((prev) => {
        const next = new Set(prev);
        next.delete(removed.codigo_barras);
        return next;
      });
    }
    updated[containerIndex].pesos.splice(pesoIndex, 1);
    setContainers(updated);
  };

  const addContainer = () => {
    setContainers([...containers, getInitialContainer()]);
  };

  const removeContainer = (containerIndex: number) => {
    if (containers.length <= 1) return;

    const container = containers[containerIndex];
    container.pesos.forEach((peso) => {
      if (peso.codigo_barras) {
        setReadBoxes((prev) => {
          const next = new Set(prev);
          next.delete(peso.codigo_barras);
          return next;
        });
      }
    });

    setContainers(containers.filter((_, index) => index !== containerIndex));
  };

  const updateContainerBarcodeValue = (containerIndex: number, newValue: string) => {
    const updated = [...containers];
    updated[containerIndex].barcodeValue = newValue;
    setContainers(updated);
  };

  const handleSubmit = () => {
    const payload = buildPayload(containers);
    const userId = user?.user_id ?? 0;

    submitMutation.mutate({
      payload,
      user_id: userId,
      send_to_warehouse: sendToWarehouse,
    });
  };

  const handleBarcodeScan = (containerIndex: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Tab' && e.key !== 'Enter') return;

    const barcode = containers[containerIndex].barcodeValue.trim();

    if (isNaN(Number(barcode))) {
      showDebugMessage(
        'Formtato do código de barras inválido (Insira apenas números).',
        'error',
      );
      updateContainerBarcodeValue(containerIndex, '');
      return;
    }

    let weightPart = '';
    let validade = '';
    if (source === 'CERMAK' && barcode.length === 36) {
      weightPart = barcode.substring(28, 32);
    } else if (source === 'CAMANCHACA' && barcode.length === 48) {
      weightPart = barcode.substring(20, 26);
      validade = barcode.substring(28, 34);
    } else if (source === 'Manual' && (barcode.length === 48 || barcode.length === 36)) {
      if (barcode.length === 36) {
        weightPart = barcode.substring(28, 32);
      } else if (barcode.length === 48) {
        weightPart = barcode.substring(20, 26);
      }
    } else {
      showDebugMessage('Código de barras inválido.', 'error');
      updateContainerBarcodeValue(containerIndex, '');
      return;
    }

    const netWeight = (parseInt(weightPart, 10) / 100).toFixed(2);

    if (readBoxes.has(barcode)) {
      showDebugMessage(`Caixa ${barcode} já foi lida.`, 'error');
      updateContainerBarcodeValue(containerIndex, '');
      return;
    }

    const updated = [...containers];
    updated[containerIndex].pesos.push({
      peso_liquido: netWeight,
      codigo_barras: barcode,
    });

    if (source === 'CAMANCHACA') {
      updated[containerIndex].validade = converterData(validade);
    }

    setContainers(updated);
    const newReadBoxes = new Set(readBoxes);
    newReadBoxes.add(barcode);
    setReadBoxes(newReadBoxes);

    showDebugMessage(`Caixa adicionada com peso ${netWeight} kg.`, 'success');
    updateContainerBarcodeValue(containerIndex, '');
  };

  const loading = submitMutation.isPending;

  return (
    <div className="coleta-pesos-container">
      <div className="coleta-pesos-toolbar">
        <h2>Coleta de Pesos</h2>

        <label
          className="coleta-pesos-checkbox"
          htmlFor="sendToWarehouse"
        >
          <input
            type="checkbox"
            id="sendToWarehouse"
            checked={sendToWarehouse}
            onChange={(e) => setSendToWarehouse(e.target.checked)}
          />
          Enviar etiquetas para o armazém
        </label>
      </div>

      {pageMessage && <p className="coleta-pesos-page-message">{pageMessage}</p>}

      {debugMessage && (
        <div className={`floating-toast ${debugMessageType}`}>{debugMessage}</div>
      )}

      <div className="coleta-pesos-content">
        {containers.map((container, containerIndex) => {
          const totals = computeTotals(container.pesos);
          const notaValue = parseFloat(container.peso_nota) || 0;
          const diferenca = notaValue - totals.total;
          const hasDiferenca = notaValue > 0 && Math.abs(diferenca) > 0.001;

          return (
            <Card
              key={containerIndex}
              className="coleta-pesos-container-card"
            >
              <Card.Header className="coleta-pesos-container-card__header">
                <div className="coleta-pesos-container-title">
                  <span
                    className="coleta-pesos-container-icon"
                    aria-hidden="true"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    </svg>
                  </span>
                  <h3>Container #{formatContainerNumber(containerIndex)}</h3>
                </div>

                {containers.length > 1 && (
                  <button
                    type="button"
                    className="coleta-pesos-remove-container"
                    onClick={() => removeContainer(containerIndex)}
                    disabled={loading}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                    Remover
                  </button>
                )}
              </Card.Header>

              <Card.Content>
                <div className="coleta-pesos-form-grid">
                  <div className="input-content">
                    <label htmlFor={`origem-${containerIndex}`}>Origem</label>
                    <select
                      id={`origem-${containerIndex}`}
                      value={source}
                      onChange={(e) => setSource(e.target.value as TBarcodeSource)}
                      disabled={loading}
                    >
                      <option value="Manual">Manual</option>
                      <option value="CERMAK">CERMAK</option>
                      <option value="CAMANCHACA">CAMANCHACA</option>
                    </select>
                  </div>

                  <div className="input-content">
                    <label htmlFor={`barcode-${containerIndex}`}>Código de Barras</label>
                    <input
                      type="text"
                      id={`barcode-${containerIndex}`}
                      value={container.barcodeValue}
                      onChange={(e) => updateContainerBarcodeValue(containerIndex, e.target.value)}
                      onKeyDown={(e) => handleBarcodeScan(containerIndex, e)}
                      placeholder="Scan..."
                      disabled={loading}
                    />
                  </div>

                  <div className="input-content">
                    <label htmlFor={`nf-ref-${containerIndex}`}>Ref. Nota Fiscal</label>
                    <input
                      type="text"
                      id={`nf-ref-${containerIndex}`}
                      value={container.nf_ref}
                      onChange={(e) => handleCommonChange(containerIndex, 'nf_ref', e.target.value)}
                      placeholder="NF-2023-991"
                      disabled={loading}
                    />
                  </div>

                  <div className="input-content">
                    <label htmlFor={`peso-nota-${containerIndex}`}>Peso Nota (kg)</label>
                    <input
                      type="text"
                      id={`peso-nota-${containerIndex}`}
                      value={container.peso_nota}
                      onChange={(e) =>
                        handleCommonChange(containerIndex, 'peso_nota', e.target.value)
                      }
                      placeholder="1500,00"
                      disabled={loading}
                    />
                  </div>

                  <div className="input-content">
                    <label htmlFor={`codigo-produto-${containerIndex}`}>Cód. Produto</label>
                    <input
                      type="text"
                      id={`codigo-produto-${containerIndex}`}
                      value={container.codigo_produto}
                      onChange={(e) =>
                        handleCommonChange(containerIndex, 'codigo_produto', e.target.value)
                      }
                      onBlur={() =>
                        updateProductDescription(containerIndex, container.codigo_produto)
                      }
                      placeholder="PRD-X92-20"
                      disabled={loading}
                    />
                  </div>

                  <div className="input-content coleta-pesos-form-grid__field--descricao">
                    <label htmlFor={`descricao-${containerIndex}`}>Descrição do Produto</label>
                    <input
                      type="text"
                      id={`descricao-${containerIndex}`}
                      value={container.description}
                      readOnly
                      placeholder="Descrição do produto"
                    />
                  </div>

                  <div className="input-content">
                    <label htmlFor={`lote-${containerIndex}`}>Lote</label>
                    <input
                      type="text"
                      id={`lote-${containerIndex}`}
                      value={container.lote}
                      onChange={(e) => handleCommonChange(containerIndex, 'lote', e.target.value)}
                      placeholder="L2023-B4"
                      disabled={loading}
                    />
                  </div>

                  <div className="input-content">
                    <label htmlFor={`cliente-${containerIndex}`}>Cliente</label>
                    <input
                      type="text"
                      id={`cliente-${containerIndex}`}
                      value={container.cliente}
                      onChange={(e) => handleCommonChange(containerIndex, 'cliente', e.target.value)}
                      placeholder="Nome do cliente"
                      disabled={loading}
                    />
                  </div>

                  <div className="input-content">
                    <label htmlFor={`classificacao-${containerIndex}`}>Classificação</label>
                    <select
                      id={`classificacao-${containerIndex}`}
                      value={container.classificacao}
                      onChange={(e) =>
                        handleCommonChange(containerIndex, 'classificacao', e.target.value)
                      }
                      disabled={loading}
                    >
                      {CLASSIFICACAO_OPTIONS.map((option) => (
                        <option
                          key={option}
                          value={option}
                        >
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="input-content">
                    <label htmlFor={`validade-${containerIndex}`}>Data de Expiração</label>
                    <input
                      type="date"
                      id={`validade-${containerIndex}`}
                      value={container.validade}
                      onChange={(e) =>
                        handleCommonChange(containerIndex, 'validade', e.target.value)
                      }
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="coleta-pesos-pesos-section">
                  <div className="coleta-pesos-pesos-header">
                    <h4>Pesos Líquidos</h4>
                    <button
                      type="button"
                      onClick={() => addPesoInput(containerIndex)}
                      className="filter-button"
                      disabled={loading}
                    >
                      + Adicionar Peso
                    </button>
                  </div>

                  <div className="coleta-pesos-table-container">
                    <div className="coleta-pesos-table-scroll">
                      <Table className="estoque-table">
                        <caption className="sr-only">
                          Pesos líquidos do Container #{formatContainerNumber(containerIndex)}
                        </caption>
                        <Table.Header>
                          <Table.Row>
                            {PESOS_TABLE_COLUMNS.map((column) => (
                              <Table.Head
                                key={column}
                                scope="col"
                              >
                                {column}
                              </Table.Head>
                            ))}
                          </Table.Row>
                        </Table.Header>

                        <Table.Content>
                          {container.pesos.length === 0 ? (
                            <Table.Row>
                              <Table.Cell colSpan={PESOS_TABLE_COLUMNS.length}>
                                Nenhum peso registrado. Escaneie um código ou adicione manualmente.
                              </Table.Cell>
                            </Table.Row>
                          ) : (
                            container.pesos.map((peso, pesoIndex) => (
                              <Table.Row key={pesoIndex}>
                                <Table.Cell>#{pesoIndex + 1}</Table.Cell>
                                <Table.Cell>
                                  <input
                                    type="number"
                                    className="coleta-pesos-peso-input"
                                    value={peso.peso_liquido}
                                    onChange={(e) =>
                                      handlePesoChange(containerIndex, pesoIndex, e.target.value)
                                    }
                                    placeholder="0.00"
                                    aria-label={`Peso líquido ${pesoIndex + 1}`}
                                    disabled={loading}
                                  />
                                </Table.Cell>
                                <Table.Cell>
                                  {peso.codigo_barras ? (
                                    <span title={peso.codigo_barras}>
                                      Leitura automática
                                    </span>
                                  ) : (
                                    <span className="coleta-pesos-observacao">
                                      Conferido manualmente
                                    </span>
                                  )}
                                </Table.Cell>
                                <Table.Cell>
                                  <button
                                    type="button"
                                    onClick={() => removePesoInput(containerIndex, pesoIndex)}
                                    className="coleta-pesos-remove-peso"
                                    aria-label={`Remover peso ${pesoIndex + 1}`}
                                    disabled={loading}
                                  >
                                    &times;
                                  </button>
                                </Table.Cell>
                              </Table.Row>
                            ))
                          )}
                        </Table.Content>
                      </Table>
                    </div>
                  </div>
                </div>

                <div className="coleta-pesos-summary">
                  <div className="coleta-pesos-summary-card">
                    <span className="coleta-pesos-summary-card__label">Total de Caixas</span>
                    <span className="coleta-pesos-summary-card__value">{totals.count}</span>
                  </div>

                  <div className="coleta-pesos-summary-card">
                    <span className="coleta-pesos-summary-card__label">Peso Total Acumulado</span>
                    <span className="coleta-pesos-summary-card__value">
                      {totals.total.toFixed(2)} kg
                    </span>
                  </div>

                  <div
                    className={`coleta-pesos-summary-card${
                      hasDiferenca ? ' coleta-pesos-summary-card--warning' : ''
                    }`}
                  >
                    <span className="coleta-pesos-summary-card__label">Diferença vs Nota</span>
                    <span className="coleta-pesos-summary-card__value">
                      {hasDiferenca ? `${diferenca > 0 ? '+' : ''}${diferenca.toFixed(2)}` : '0.00'} kg
                    </span>
                  </div>
                </div>
              </Card.Content>
            </Card>
          );
        })}
      </div>

      <div className="coleta-pesos-actions">
        <button
          type="button"
          onClick={addContainer}
          className="clear-filter-button"
          disabled={loading}
        >
          Adicionar Novo Container
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          className="filter-button"
          disabled={loading}
        >
          Enviar Pesos
        </button>
      </div>

      {loading && (
        <Modal
          className="coleta-pesos-loading-modal"
          onClose={() => {}}
          closeOnOverlayClick={false}
          closeOnEscape={false}
        >
          <Modal.Content>
            <div className="coleta-pesos-loading">
              <div className="spinner" />
              <p>Enviando Dados...</p>
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}
